import { inject, injectable } from "inversify";
import { Config } from "../../config/config";
import { TYPES } from "../di/types";
import { ResourceLoader } from "../loader/resourceLoader/ResourceLoader";
import { AnalyticsEvent } from "./AnalyticEvent";
import { AnalyticsSender } from "./AnalyticsSender";

declare global {
  interface Window {
    vintomGADataLayer: any[];
    vintomGtag: (...args: any) => void;
  }
}
let eventCategory: string;

export interface GoogleAnalyticsSender extends AnalyticsSender {
  initialize(gaServiceTrackers: string[], campaign: string): Promise<void>;
}

@injectable()
export class GoogleAnalyticsSenderImpl implements GoogleAnalyticsSender {

  constructor(@inject(TYPES.loader.resourceLoader.ResourceLoader) private resourceLoader: ResourceLoader) { }

  initialize(gaServiceTrackers: string[], campaign: string): Promise<void> {
    return this.resourceLoader.loadResources([{ location: Config.gaEndpoint, type: "JS" }])
      .then(() => {
        window.vintomGADataLayer = window.vintomGADataLayer || [];

        // tslint:disable:only-arrow-functions
        window.vintomGtag = function() {
          window.vintomGADataLayer.push(arguments);
        };
        // tslint:enable:only-arrow-functions
        if (campaign !== "MBBANKFULL2") {
          window.vintomGtag("js", new Date());
        }

        for (const gaServiceTracker of gaServiceTrackers) {
          window.vintomGtag("config", gaServiceTracker, {
            campaign: {
              source: campaign
            },
            anonymize_ip: true
          });
          eventCategory = campaign;
        }
      });
  }

  sendEvent(event: AnalyticsEvent): void {
    const eventAction =
      event.type === "SCENARIO_ELEMENT_PROGRESS"
        ? `${event.type}_${event.progress}`
        : event.type;

    const eventLabel = this.serializeEventToAction(event);

    if (eventAction !== undefined) {
      if ((eventCategory === "MBBANKFULL2" && eventAction === "PLAYER_INIT") ||
          (eventCategory === "MBBANKFULL2" && eventAction === "SCENARIO_ELEMENT_PLAYING")) {
        return;
      }
      if (eventCategory === "MBBANKFULL2") {
        window.vintomGtag("js", new Date());
      }
      window.vintomGtag("event", eventAction, {
        event_category: eventCategory,
        event_label: eventLabel
      });
    }
  }

  private serializeEventToAction(event: AnalyticsEvent): string {
    const base = `scenario element id: ${event.scenarioElementId}`;

    switch (event.type) {
      case "PLAYER_INIT":
        return base;
      case "PLAYER_START":
        return base;
      case "PLAYER_ERROR":
        return `${base}, message: ${event.errorMessage}`;
      case "SCENARIO_ELEMENT_START":
        return base;
      case "SCENARIO_ELEMENT_PROGRESS":
        return base;
      case "CTA_SHOW":
        return `${base}, cta id: ${event.ctaId}`;
      case "CTA_ACTION":
        return `${base}, cta id: ${event.ctaId}, action id: ${event.actionId}`;
      case "NOT_SUPPORTED_BROWSER":
        return base;
      default:
        // all other events are ignored by GoogleAnalyticsSender
        return undefined;
    }
  }
}
